import * as Sentry from '@sentry/react';

/**
 * Capture an error in Sentry with optional context.
 */
export function captureError(
  error: unknown,
  context?: string,
  options?: {
    extra?: Record<string, unknown>;
    tags?: Record<string, string>;
  }
) {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setTag('context', context);
      if (options?.tags) {
        Object.entries(options.tags).forEach(([key, value]) => scope.setTag(key, value));
      }
      if (options?.extra) {
        Object.entries(options.extra).forEach(([key, value]) => scope.setExtra(key, value));
      }
      Sentry.captureException(error);
    });
  } else {
    if (options?.tags || options?.extra) {
      Sentry.withScope((scope) => {
        if (options?.tags) {
          Object.entries(options.tags).forEach(([key, value]) => scope.setTag(key, value));
        }
        if (options?.extra) {
          Object.entries(options.extra).forEach(([key, value]) => scope.setExtra(key, value));
        }
        Sentry.captureException(error);
      });
      return;
    }

    Sentry.captureException(error);
  }
}

/**
 * Capture an informational/warning message in Sentry with structured extra data.
 * Useful for tracking non-error events like duplicate detection.
 */
export function captureMessage(
  message: string,
  options?: {
    level?: Sentry.SeverityLevel;
    context?: string;
    extra?: Record<string, unknown>;
    tags?: Record<string, string>;
  }
) {
  Sentry.withScope((scope) => {
    if (options?.context) {
      scope.setTag('context', options.context);
    }
    if (options?.tags) {
      Object.entries(options.tags).forEach(([key, value]) => scope.setTag(key, value));
    }
    if (options?.extra) {
      Object.entries(options.extra).forEach(([key, value]) => scope.setExtra(key, value));
    }
    scope.setLevel(options?.level || 'warning');
    Sentry.captureMessage(message);
  });
}

/**
 * Add breadcrumb context without generating a standalone Sentry event.
 * Useful for high-frequency lifecycle signals like sync start/complete.
 */
export function addBreadcrumb(
  message: string,
  options?: {
    level?: Sentry.SeverityLevel;
    category?: string;
    data?: Record<string, unknown>;
  }
) {
  Sentry.addBreadcrumb({
    message,
    level: options?.level || 'info',
    category: options?.category || 'app',
    data: options?.data
  });
}
